import type { ColumnDef, EnemyDef, LevelDef, PickupDef, SegmentDef } from '../../types';

// OP-303 La Plaza (GDD §10.5, §10.2, issue #123): the approach through the colonial city at dusk, written by hand
// with the format of §15.6. 400 u of approach at 6.5 u/s (6.0 of world 3 plus 0.5 of level 3; takeoff, warm-up 18 %,
// tension 26 % up to CP-303-A at x 200, respite 15 %, peak 41 %), CP-303-B 5 u before the arena, the arena of JEFE-3
// (open length: the fight ends it), the El Paso sky of 120 u with bonus bills and the jump zone. Columns: colonial
// buildings (`w3-colonial`) with the 3.5 u gap of world 3 every 8 u, the gap centre never moving more than 2.5 u (the
// world allows 3.5). Hazards of the approach: balconies of Don Nieto (EN-32) in the colonial buildings, a hitman plane
// (EN-03, announced with INTERCEPT) and the federales (EN-02); the peak adds a crop duster (EN-31). No tequila train
// (EN-33, propio de las vias). An armor before the checkpoint and a bribe in the respite; no scapular (it goes in
// OP-302). All x are local.

const column = (x: number, gapY: number): ColumnDef => ({ x, gapY, gapH: 3.5, skin: 'w3-colonial' });
const dollar = (x: number, y: number): PickupDef => ({ x, y, type: 'dollar' });
const armor = (x: number, y: number): PickupDef => ({ x, y, type: 'armor' });
const bribe = (x: number, y: number): PickupDef => ({ x, y, type: 'bribe' });
/** Don Nieto's balcony (EN-32), in the sky beside the colonial buildings: it throws sombreros that come back. */
const balcony = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-32' });
/** A hitman plane (EN-03), called by INTERCEPT. */
const hitman = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-03' });
/** A crop duster (EN-31), in the peak only. */
const duster = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-31' });
/** A helicopter of the federales (EN-02), hovering high. */
const federales = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-02' });

const takeoff: SegmentDef = { id: 'w3-plaza-takeoff-01', kind: 'takeoff', length: 40, columns: [], enemies: [], pickups: [] };

const warmup01: SegmentDef = {
  id: 'w3-plaza-warmup-01',
  kind: 'pattern',
  phase: 'warmup',
  length: 66,
  columns: [
    column(20.0, 6.0),
    column(28.0, 6.5),
    column(36.0, 6.0),
    column(44.0, 5.5),
    column(52.0, 6.0),
    column(60.0, 6.5),
  ],
  enemies: [],
  pickups: [
    dollar(31.00, 6.25),
    dollar(32.00, 6.55),
    dollar(33.00, 6.25),
    dollar(47.00, 5.75),
    dollar(48.00, 6.05),
    dollar(49.00, 5.75),
  ],
};

const tension01: SegmentDef = {
  id: 'w3-plaza-tension-01',
  kind: 'pattern',
  phase: 'tension',
  length: 47,
  columns: [
    column(2.0, 7.0),
    column(10.0, 6.0),
    column(18.0, 7.5),
    column(26.0, 6.0),
    column(34.0, 8.0),
    column(42.0, 6.5),
  ],
  enemies: [balcony(22.0, 9.5)],
  pickups: [
    dollar(13.00, 6.75),
    dollar(14.00, 7.05),
    dollar(15.00, 6.75),
    dollar(29.00, 7.00),
    dollar(30.00, 7.30),
    dollar(31.00, 7.00),
  ],
};

const tension02: SegmentDef = {
  id: 'w3-plaza-tension-02',
  kind: 'pattern',
  phase: 'tension',
  length: 47,
  columns: [
    column(3.0, 7.0),
    column(11.0, 5.5),
    column(19.0, 7.0),
    column(27.0, 5.5),
    column(35.0, 7.5),
    column(43.0, 6.0),
  ],
  enemies: [hitman(31.0, 9.5)],
  pickups: [
    dollar(14.00, 6.25),
    dollar(15.00, 6.55),
    dollar(16.00, 6.25),
    dollar(30.00, 6.50),
    dollar(31.00, 6.80),
    dollar(32.00, 6.50),
    armor(7.0, 6.25),
  ],
};

const respite01: SegmentDef = {
  id: 'w3-plaza-respite-01',
  kind: 'pattern',
  phase: 'respite',
  length: 54,
  columns: [
    column(4.0, 6.0),
    column(12.0, 6.5),
    column(20.0, 6.0),
    column(28.0, 6.5),
    column(36.0, 6.0),
    column(44.0, 6.5),
  ],
  enemies: [],
  pickups: [
    dollar(15.00, 6.25),
    dollar(16.00, 6.55),
    dollar(17.00, 6.25),
    dollar(31.00, 6.25),
    dollar(32.00, 6.55),
    dollar(33.00, 6.25),
    bribe(16.0, 6.25),
  ],
};

const peak01: SegmentDef = {
  id: 'w3-plaza-peak-01',
  kind: 'pattern',
  phase: 'peak',
  length: 73,
  columns: [
    column(2.0, 8.0),
    column(10.0, 6.0),
    column(18.0, 8.0),
    column(26.0, 6.0),
    column(34.0, 4.5),
    column(42.0, 7.0),
    column(50.0, 5.0),
    column(58.0, 7.5),
    column(66.0, 6.0),
  ],
  enemies: [federales(14.0, 11.2), balcony(46.0, 9.5)],
  pickups: [
    dollar(13.00, 7.00),
    dollar(14.00, 7.30),
    dollar(15.00, 7.00),
    dollar(29.00, 5.25),
    dollar(30.00, 5.55),
    dollar(31.00, 5.25),
  ],
};

const peak02: SegmentDef = {
  id: 'w3-plaza-peak-02',
  kind: 'pattern',
  phase: 'peak',
  length: 73,
  columns: [
    column(2.0, 7.0),
    column(10.0, 5.5),
    column(18.0, 7.5),
    column(26.0, 6.0),
    column(34.0, 4.5),
    column(42.0, 7.0),
    column(50.0, 5.5),
  ],
  enemies: [duster(14.0, 8.0), federales(30.0, 11.2)],
  pickups: [
    dollar(13.00, 6.50),
    dollar(14.00, 6.80),
    dollar(15.00, 6.50),
    dollar(29.00, 5.25),
    dollar(30.00, 5.55),
    dollar(31.00, 5.25),
  ],
};

/** The arena of JEFE-3: no gap columns outside the check columns of its final phase; the fight lasts until the boss retreats (§9.1). */
const arena: SegmentDef = { id: 'w3-plaza-arena-01', kind: 'boss-arena', length: 600, columns: [], enemies: [], pickups: [] };

/** The sky of El Paso (§11.1): 120 u of free flight with bonus bills. */
const usSky: SegmentDef = {
  id: 'w3-plaza-us-sky-01',
  kind: 'us-sky',
  length: 120,
  columns: [],
  enemies: [],
  pickups: [dollar(20, 6), dollar(21, 6.4), dollar(22, 6.8), dollar(60, 8), dollar(61, 8.4), dollar(62, 8.8), dollar(100, 6), dollar(101, 5.6), dollar(102, 5.2)],
};

/** The jump zone of El Salto (§11.1 steps 2-6): the X on the ground and room for the fall. */
const jumpZone: SegmentDef = { id: 'w3-plaza-jump-01', kind: 'jump', length: 60, columns: [], enemies: [], pickups: [] };

export const OP_303_SEGMENTS: readonly SegmentDef[] = [takeoff, warmup01, tension01, tension02, respite01, peak01, peak02, arena, usSky, jumpZone];

export const OP_303: LevelDef = {
  id: '3-3',
  op: 'OP-303',
  name: 'La Plaza',
  world: 3,
  scrollSpeed: 6.5,
  timeOfDay: 'dusk',
  segments: OP_303_SEGMENTS.map((segment) => segment.id),
  checkpoints: [
    { id: 'CP-303-A', x: 200 },
    { id: 'CP-303-B', x: 395 },
  ],
  ending: 'boss-and-jump',
  bossId: 'JEFE-3',
};
